import { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import moment from "moment";
import { getAppointments } from "../../../api/api";
import DataTable from "../../../components/DataTable";
import AppointmentFilters from "../../../components/AppointmentFilters";
import SegmentedControl from "../../../components/SegmentedControl";
import { Badge } from "../../../components/ui/badge";
import {
  APPOINTMENT_STATUS,
  ACTIVE_STATUSES,
  STATUS_LABEL,
  VISIT_TYPE_LABEL,
} from "../../../lib/appointment-filters";

const TABS = [
  { value: "upcoming", label: "Upcoming", statuses: ACTIVE_STATUSES, countKey: "upcoming" },
  { value: "done", label: "Done", statuses: [APPOINTMENT_STATUS.COMPLETED], countKey: "done" },
  { value: "cancelled", label: "Cancelled", statuses: [APPOINTMENT_STATUS.CANCELLED], countKey: "cancelled" },
  { value: "all", label: "All", statuses: null, countKey: "all" },
];

// Sent when the chosen statuses can't occur inside the active tab — matches
// no row (the truthful answer) rather than silently dropping the filter.
const MATCH_NOTHING = "__none__";

// departmentIds stays permanently empty — AppointmentFilters expects the key
// to exist (its chips logic does departmentIds.forEach(...)) even though a
// doctor's own view has no department filter to offer.
const EMPTY_FILTERS = { search: "", from: "", to: "", departmentIds: [], statuses: [], visitTypes: [], delayStatuses: [] };

const readFilters = (params) => ({
  search: params.get("q") || "",
  from: params.get("from") || "",
  to: params.get("to") || "",
  departmentIds: [],
  statuses: params.get("status")?.split(",").filter(Boolean) || [],
  visitTypes: params.get("type")?.split(",").filter(Boolean) || [],
  delayStatuses: params.get("delay")?.split(",").filter(Boolean) || [],
});

const writeParams = (filters, tab, page) => {
  const next = new URLSearchParams();
  if (filters.search) next.set("q", filters.search);
  if (filters.from) next.set("from", filters.from);
  if (filters.to) next.set("to", filters.to);
  if (filters.statuses.length) next.set("status", filters.statuses.join(","));
  if (filters.visitTypes.length) next.set("type", filters.visitTypes.join(","));
  if (filters.delayStatuses.length) next.set("delay", filters.delayStatuses.join(","));
  if (tab !== "upcoming") next.set("tab", tab);
  if (page > 1) next.set("page", String(page));
  return next;
};

// Read-only — a doctor can see their own list but check-in/complete/cancel/
// edit/invoice stay caredesk-only, both here (no action buttons) and at the
// API layer (restrictQueryToOwnDoctor already forces doctor_id server-side).
// Filters mirror the standard Appointments page (search/date/status/type/
// delay) minus Provider/Department, which don't apply to a single doctor's
// own view.
export default function MyAppointments() {
  const doctorId = useSelector((s) => s.users.user?.doctor_id);
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState(() => readFilters(searchParams));
  const [tab, setTab] = useState(() => searchParams.get("tab") || "upcoming");
  const [page, setPage] = useState(() => Number(searchParams.get("page")) || 1);

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ total: 0, numberOfPage: 1 });
  const [counts, setCounts] = useState({ upcoming: 0, done: 0, cancelled: 0, all: 0 });

  const patch = useCallback((partial) => {
    setFilters((prev) => ({ ...prev, ...partial }));
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  }, []);

  const hasFilters = useMemo(
    () =>
      Boolean(filters.search || filters.from || filters.to) ||
      filters.statuses.length > 0 ||
      filters.visitTypes.length > 0 ||
      filters.delayStatuses.length > 0,
    [filters]
  );

  // The status filter narrows *within* the active tab rather than replacing
  // it, so "Upcoming + Checked In" means what it looks like it means.
  const effectiveStatuses = useMemo(() => {
    const tabStatuses = TABS.find((t) => t.value === tab)?.statuses;
    if (!filters.statuses.length) return tabStatuses;
    if (!tabStatuses) return filters.statuses;
    const intersection = filters.statuses.filter((s) => tabStatuses.includes(s));
    return intersection.length ? intersection : [MATCH_NOTHING];
  }, [tab, filters.statuses]);

  useEffect(() => {
    const next = writeParams(filters, tab, page).toString();
    if (next !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [filters, tab, page, searchParams, setSearchParams]);

  const queryParams = useMemo(
    () => ({
      doctor_id: doctorId,
      status: effectiveStatuses?.length ? effectiveStatuses.join(",") : undefined,
      visit_type: filters.visitTypes.length ? filters.visitTypes.join(",") : undefined,
      delay_status: filters.delayStatuses.length ? filters.delayStatuses.join(",") : undefined,
      from: filters.from || undefined,
      to: filters.to || undefined,
      search: filters.search || undefined,
    }),
    [doctorId, effectiveStatuses, filters]
  );

  const load = useCallback(() => {
    if (!doctorId) return;
    setLoading(true);
    getAppointments({ ...queryParams, page, limit: 15 })
      .then((res) => {
        setAppointments(res.data);
        setMeta({ total: res.total, numberOfPage: res.numberOfPage });
        if (res.counts) setCounts(res.counts);
      })
      .catch((err) => toast.error(err?.message || "Could not load appointments."))
      .finally(() => setLoading(false));
  }, [queryParams, page, doctorId]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const columns = [
    {
      key: "patient",
      header: "Patient",
      render: (a) => (
        <div>
          <p className="font-medium">{a.patient_id?.name}</p>
          <p className="text-xs text-muted-foreground">{a.patient_id?.phone}</p>
        </div>
      ),
    },
    {
      key: "when",
      header: "Date & time",
      render: (a) => (
        <div>
          <p>{moment(a.iso_date).format("D MMM YYYY")}</p>
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            {a.visit_type === "walkin" && a.checked_in_at ? moment(a.checked_in_at).format("hh:mm A") : a.slot_time}
            {a.is_vip && (
              <Badge variant="warning" className="px-1.5 py-0 text-[10px]">
                VIP
              </Badge>
            )}
          </p>
          {a.created_by?.name && <p className="text-xs text-muted-foreground">Booked by {a.created_by.name}</p>}
        </div>
      ),
    },
    { key: "visit_type", header: "Visit type", render: (a) => VISIT_TYPE_LABEL[a.visit_type] || a.visit_type },
    {
      key: "status",
      header: "Status",
      render: (a) => <Badge variant="outline">{STATUS_LABEL[a.status] || a.status}</Badge>,
    },
  ];

  const tabItems = TABS.map((t) => ({ ...t, count: counts[t.countKey] ?? 0 }));

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-serif text-2xl font-semibold">My Appointments</h2>
        <p className="text-sm text-muted-foreground">Your own upcoming and past consultations</p>
      </div>

      <AppointmentFilters value={filters} patch={patch} onReset={resetFilters} doctors={[]} showProvider={false} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentedControl
          items={tabItems}
          value={tab}
          onChange={(v) => {
            setTab(v);
            setPage(1);
          }}
        />
        {!loading && (
          <p className="text-sm text-muted-foreground">
            {meta.total} {meta.total === 1 ? "result" : "results"}
          </p>
        )}
      </div>

      <DataTable
        columns={columns}
        data={appointments}
        loading={loading}
        emptyMessage={hasFilters ? "No appointments match these filters." : "No appointments here."}
        page={page}
        numberOfPage={meta.numberOfPage}
        onPageChange={setPage}
        rowKey={(a) => a._id}
      />
    </div>
  );
}
